import React from "react";
import { Box, Typography, Card, CardContent, Tooltip } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import { operationsDashboardStyles, getStatusColor } from "../../styles";
import { BaseCardProps } from "../../type";

export const BaseCard: React.FC<BaseCardProps> = ({
  data,
  children,
  headerRight,
  isClickable,
}) => {
  const cardIsClickable = isClickable ?? data.isClickable ?? true;
  const iconRef = React.useRef<SVGSVGElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleCardClick = () => {
    if (!cardIsClickable) return;

    if (data.onClick) {
      data.onClick();
    }
  };

  const getCardStyles = () => {
    const statusColor = data.status ? getStatusColor(data.status) : null;

    return {
      ...operationsDashboardStyles.metricCard,
      ...operationsDashboardStyles.baseCardHeight,
      ...(cardIsClickable && operationsDashboardStyles.baseCardClickable),
      "&:hover": cardIsClickable
        ? {
            ...operationsDashboardStyles.metricCard["&:hover"],
            ...(statusColor && {
              borderTop: `0.1875rem solid ${statusColor}`,
            }),
          }
        : {},
    };
  };

  const cardContent = (
    <Box
      ref={cardRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ width: "100%", height: "100%" }}
    >
      <Card
        onClick={cardIsClickable ? handleCardClick : undefined}
        sx={getCardStyles()}
      >
        <CardContent sx={operationsDashboardStyles.metricCardContent}>
          {data.status && (
            <Box
              sx={{
                ...operationsDashboardStyles.statusIndicator,
                bgcolor: getStatusColor(data.status),
              }}
            />
          )}

          <Box sx={operationsDashboardStyles.flexRow}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={operationsDashboardStyles.cardTitle}>
                {data.title}
              </Typography>
              {data.infoTooltip && (
                <InfoOutlineIcon
                  ref={iconRef}
                  sx={{
                    fontSize: "16px",
                    color: "text.secondary",
                    paddingBottom: "0.5rem",
                    lineHeight: 1,
                  }}
                />
              )}
            </Box>
            {data.subtitle && (
              <Typography sx={operationsDashboardStyles.cardSubtitle}>
                {data.subtitle}
              </Typography>
            )}
            {headerRight && (
              <Box sx={operationsDashboardStyles.headerRight}>{headerRight}</Box>
            )}
          </Box>
          <Box sx={operationsDashboardStyles.flex1}>{children}</Box>
        </CardContent>
      </Card>
    </Box>
  );

  if (data.infoTooltip) {
    return (
      <Tooltip
        title={data.infoTooltip}
        arrow
        placement="right"
        open={isHovered && iconRef.current !== null}
        PopperProps={{
          anchorEl: iconRef.current,
          placement: "right",
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [-4, -7],
              },
            },
          ],
        }}
      >
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
}
